export interface StreakState {
  current: number;
  longest: number;
  lastPlayedDate: string | null; // YYYY-MM-DD in Europe/Budapest
  graceUsed: boolean;
  daysSinceGraceReset: number;
}

const GRACE_RESET_DAYS = 7;

export function initialStreak(): StreakState {
  return {
    current: 0,
    longest: 0,
    lastPlayedDate: null,
    graceUsed: false,
    daysSinceGraceReset: 0,
  };
}

/**
 * Returns a new StreakState given today's date. Idempotent for the same date
 * (re-calling with today's date does not re-increment). Does not mutate input.
 * See spec §2.7.
 */
export function updateStreak(prev: StreakState, today: string): StreakState {
  if (prev.lastPlayedDate === today) {
    return prev;
  }

  if (prev.lastPlayedDate === null) {
    return {
      current: 1,
      longest: Math.max(prev.longest, 1),
      lastPlayedDate: today,
      graceUsed: false,
      daysSinceGraceReset: 0,
    };
  }

  const gap = daysBetween(prev.lastPlayedDate, today);
  if (gap < 0) {
    throw new Error(
      `updateStreak: today (${today}) is before lastPlayedDate (${prev.lastPlayedDate})`,
    );
  }

  if (gap === 1) {
    // Consecutive day — streak continues.
    const next: StreakState = {
      current: prev.current + 1,
      longest: Math.max(prev.longest, prev.current + 1),
      lastPlayedDate: today,
      graceUsed: prev.graceUsed,
      daysSinceGraceReset: prev.daysSinceGraceReset + 1,
    };
    if (next.daysSinceGraceReset >= GRACE_RESET_DAYS && next.graceUsed) {
      next.graceUsed = false;
      next.daysSinceGraceReset = 0;
    }
    return next;
  }

  if (gap === 2 && !prev.graceUsed) {
    // 1-day miss with grace available — preserve and grow streak by 1.
    return {
      current: prev.current + 1,
      longest: Math.max(prev.longest, prev.current + 1),
      lastPlayedDate: today,
      graceUsed: true,
      daysSinceGraceReset: 0,
    };
  }

  // Streak broken (gap > 2, or gap === 2 with grace already spent).
  return {
    current: 1,
    longest: Math.max(prev.longest, 1),
    lastPlayedDate: today,
    graceUsed: false,
    daysSinceGraceReset: 0,
  };
}

function daysBetween(a: string, b: string): number {
  const aMs = parseUTC(a).getTime();
  const bMs = parseUTC(b).getTime();
  return Math.round((bMs - aMs) / 86_400_000);
}

function parseUTC(date: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}
